import React, { useCallback, useEffect, useState } from 'react';
import UserInfoRow from './UserInfoRow';
import MailNotification from './MailNotification';
import LanguageSelector from '../../../components/common/LanguageSelector';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { getOwnInfo } from '../../../redux/slices/ownInfo';
import getRole from '../../../utils/getRole';
import { LANGUAGE } from '../../../constants';
import type { OrganizationUserType } from '../../../types/user';

const UserInfoTable = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { ownInfo } = useSelector((state: RootState) => state.ownInfo);
  const [userId, setUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userOrganizationName, setUserOrganizationName] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUserType[]>([]);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * 初期データを取得する
   */
  useEffect(() => {
    dispatch(getOwnInfo());
  }, []);

  /**
   * User情報が取得できたらセットする
   */
  useEffect(() => {
    if (ownInfo !== undefined) {
      // 表示ロール名を取得する
      const roleName: string = getRole(ownInfo.roles);
      // 組織名は「会社名」＋「組織詳細名」＋「（商社名）」
      const organizationName: string = ownInfo.organizations.length
        ? ownInfo.organizations
            .map((org) => {
              const baseName: string = org.organizationBaseName;
              const orgName: string = org.organizationSuffixName;
              const tradingCompanyName: string = currentLang.value === LANGUAGE.JA ? org.tradingCompanyNameJa : org.tradingCompanyNameEn ? org.tradingCompanyNameEn : org.tradingCompanyNameJa;
              const displayName: string = baseName + ' ' + orgName + '（' + tradingCompanyName + '）';
              return displayName;
            })
            .join(', ')
        : '-';
      setUserId(ownInfo.id);
      setUserName(ownInfo.username);
      setUserEmail(ownInfo.email);
      setUserRole(roleName);
      setUserOrganizationName(organizationName);
      setOrganizationUsers(ownInfo.organizations);
    }
  }, [ownInfo]);

  // EVENT HANDLER
  // ================================================================================

  // VIEW
  // ================================================================================

  return (
    <table id="userInfoTable" className="table mb-0">
      <tbody>
        <UserInfoRow header={t('user.id')} headerClass={'border-0 w-25'} data={userId} dataClass={'border-0'} />
        <UserInfoRow header={t('user.name')} data={userName} />
        <UserInfoRow header={t('user.email')} data={userEmail} />
        <UserInfoRow header={t('user.password')} data={t('user.resetPassword')} href={'/forgot-password'} userIcon={true} />
        <UserInfoRow header={t('organization.organization')} data={userOrganizationName} />
        <UserInfoRow header={t('user.role')} data={userRole} />
        <UserInfoRow header={t('user.emailNotification')} data={<MailNotification organizationUsers={organizationUsers} />} userIcon={true} />
        <UserInfoRow header={t('user.language')} data={<LanguageSelector />} userIcon={true} />
      </tbody>
    </table>
  );
};

export default UserInfoTable;
